import { BackHandOutlined } from '@mui/icons-material'
import { alpha, Box } from '@mui/material'
import { FC } from 'react'
import { useTranslate } from 'react-admin'

export interface ManualInspectionMarkerProps {
  /**
   * Whether SOSTRAT has seen the inspection — `seen_in_inspections_sostrat`,
   * surfaced as `ActiveInspection.seenInSostrat`. Drives the treatment and the
   * name, nothing else.
   */
  synced?: boolean
}

/**
 * Marks an inspection created in the app rather than by SOSTRAT — the
 * `is_local` column, surfaced as `ActiveInspection.isLocal`. It carries the
 * second fact the operator reads off the same glyph: the bare hand is created in
 * the app, not yet seen by SOSTRAT; the green hand in a green circle is created
 * in the app and seen by SOSTRAT, which the sync latches for good on its first
 * sighting, whatever the status.
 *
 * The synced state is a ringed badge rather than the same glyph in another hue,
 * because two hands differing only in colour are hard to tell apart in a column
 * of rows — the ring changes the shape, which reads at a glance and survives a
 * monochrome print. Its green is the theme's success colour, the neutral hand
 * the secondary text colour, so both follow the palette rather than a literal.
 * The app's `ManualInspectionBadge` reserves orange for "needs attention" and
 * blue for "not yet uploaded", and draws the same ringed shape: the glyph is the
 * MUI counterpart of the `hand-left-outline` Ionicons glyph the app draws.
 *
 * `titleAccess` is what carries the meaning to both audiences at once — it
 * renders an SVG `<title>`, which is the hover tooltip and the accessible name,
 * and it is also what drops the `aria-hidden` MUI puts on a decorative icon.
 * Each state names itself there, because neither colour nor shape reaches a
 * screen reader. Only an inspection the app created renders the marker, so its
 * absence is the other half of the signal and needs no glyph of its own.
 *
 * Sized in `inherit` and layout-agnostic: it takes the surrounding font size so
 * it never grows a table row, and the call site places it. The ring is drawn in
 * `em` for the same reason — it scales with the text it sits beside instead of
 * pinning the row to a pixel height.
 */
export const ManualInspectionMarker: FC<ManualInspectionMarkerProps> = ({ synced = false }) => {
  const translate = useTranslate()

  const icon = (
    <BackHandOutlined
      data-testid="manual-inspection-marker"
      data-synced={synced}
      fontSize="inherit"
      titleAccess={translate(`resources.inspections.enums.origin.${synced ? 'MANUAL_SYNCED' : 'MANUAL'}`)}
      sx={{ color: synced ? 'success.dark' : 'text.secondary', display: 'block' }}
    />
  )

  if (!synced) return icon

  return (
    <Box
      component="span"
      sx={theme => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.15em',
        borderRadius: '50%',
        border: '0.08em solid',
        borderColor: 'success.main',
        backgroundColor: alpha(theme.palette.success.main, 0.14),
      })}
    >
      {icon}
    </Box>
  )
}
